import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import {
    fetchLikedDesignCountByFolder,
    updateFavourite
} from '../../services/v3/Favourite/FavouriteService'
import { closeLoader, openLoader } from '../../redux_toolkit/Loader'
import { ReactComponent as InfoIcon } from '../../../assets/icons/info.svg'
import {
    capitalizeFirstLetter,
    changeDateFormat,
    getColorWiseActivityStatusOfSample,
    isAllIdExistBetweenTwoList,
    isValidInput,
    getShortName,
    getQuoteStatusColor,
    isPickAndChooseBuyer,
    adjustTextAreaHeight
} from '../../helpers/Util'
import Tooltip from '@mui/material/Tooltip'
import DesignImages from '../common/DesignImages'
import PackSetOrTag from '../common/PackOrSetTag'
import EyeIcon from '../../common/EyeIcon'
import SortIcon from '../../common/SortIcon'

const TableView = ({
    favouriteData,
    setFavouriteData,
    categoryName = '',
    selectedIds,
    toggleSelection,
    toggleAllSelection,
    onClickDesign,
    isDisabled,
    groupInfo,
    handleProductShowHide,
    getLikedProductCount,
    selectedProductList,
    setSelectedProductList,
    folderInfo
}) => {
    const [sortOrder, setSortOrder] = useState(null)
    const [screenWidth, setScreenWidth] = useState(window.innerWidth)
    const prevTarget = useRef(null)

    const { id } = useParams()
    const dispatch = useDispatch()

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth)
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const updateFavItemData = (e, item, type, updatedData) => {
        if (updatedData === undefined) return []
        const index = updatedData?.findIndex(
            (dataItem) => dataItem.id === item.id
        )

        if (index !== -1) {
            if (type === 'quantity') {
                const currentRemarks = updatedData[index]['remarks'] || ''
                updatedData[index] = {
                    ...updatedData[index],
                    quantity: e.target.value,
                    remarks: currentRemarks
                }
            } else if (type === 'remarks') {
                const currentQuantity = updatedData[index]['quantity'] || null
                updatedData[index] = {
                    ...updatedData[index],
                    quantity: currentQuantity,
                    remarks: e.target.value
                }
            }
            return updatedData
        }

        return updatedData
    }

    const onChangeItem = (e, item, type) => {
        let updatedData = [...favouriteData]
        setFavouriteData(
            updateFavItemData(e, item, type, updatedData),
            item?.id
        )
        if (selectedProductList !== undefined) {
            let updatedSelectedList = [...selectedProductList]
            setSelectedProductList(
                updateFavItemData(e, item, type, updatedSelectedList)
            )
        }
    }

    const isSoldFolder = () => {
        return folderInfo?.name === 'Sold Interest'
    }

    const onUpdateFavourite = async (item) => {
        try {
            dispatch(openLoader())
            const queryParams = []
            let apiParams = ''

            if (id && id !== 'my-favourite') {
                queryParams.push(
                    `folderId=${id}&addedToFavouriteById=${item?.addedToFavouriteById}`
                )
            }
            if (item?.quantity) {
                queryParams.push(`quantity=${item?.quantity}`)
            }

            queryParams.push(`remarks=${encodeURIComponent(item?.remarks)}`)

            const queryString = queryParams.join('&')

            if (queryString) {
                apiParams += `${item?.id}?${queryString}`
            }
            const response = await updateFavourite(apiParams)
            const updatedFavouriteData = favouriteData?.map((product) => {
                if (product?.id === response?.id) {
                    if (product?.quantity) {
                        return { ...product, quantityError: false }
                    }
                }
                return product
            })
            setFavouriteData(updatedFavouriteData, item?.id)
            getLikedProductCount()
            dispatch(closeLoader())
        } catch (error) {
            dispatch(closeLoader())
        }
    }

    const getOrderStatus = {
        PENDING: 'purple-3',
        RUNNING: 'teal-2',
        ON_SHIPMENT: 'blue-3',
        COMPLETED: 'green-3',
        CANCELLED: 'red-3'
    }

    const handleSort = () => {
        if (sortOrder === null) {
            setSortOrder('asc')
        } else if (sortOrder === 'asc') {
            setSortOrder('desc')
        } else if (sortOrder === 'desc') {
            setSortOrder('asc')
        }
    }

    const sortedData = [...favouriteData].sort((a, b) => {
        const qtyA = a?.quantity ? a.quantity : -1
        const qtyB = b?.quantity ? b.quantity : -1

        if (sortOrder === 'asc') {
            if (qtyA === -1) return 1
            if (qtyB === -1) return -1
            return qtyA - qtyB
        } else if (sortOrder === 'desc') {
            return qtyB - qtyA
        }
        return 0
    })

    const isAllChecked = () => {
        if (
            selectedIds?.length > 0 &&
            favouriteData.length === selectedIds.length
        ) {
            let allIds = favouriteData?.map((item) => item.id)
            return isAllIdExistBetweenTwoList(allIds, selectedIds)
        }
        return false
    }

    const renDerQuantity = (item, rowSpan = null) => {
        return (
            <td rowSpan={rowSpan} className='qty-column'>
                <input
                    type='number'
                    value={item?.quantity ?? ''}
                    onChange={(e) => onChangeItem(e, item, 'quantity')}
                    className={`${item.quantityError ? 'required' : ''}`}
                    onFocus={(event) =>
                        (prevTarget.current = event.target.value)
                    }
                    onBlur={(e) => {
                        if (!isValidInput(prevTarget, e)) {
                            prevTarget.current = null
                            return
                        }
                        onUpdateFavourite(item)
                    }}
                    disabled={isSoldFolder()}
                />
            </td>
        )
    }

    const renderRemarks = (item, rowSpan = null) => {
        return (
            <td rowSpan={rowSpan} className='remarks-column'>
                <textarea
                    value={item?.remarks ?? ''}
                    onChange={(e) => onChangeItem(e, item, 'remarks')}
                    onFocus={(event) =>
                        (prevTarget.current = event.target.value)
                    }
                    onInput={(e) => adjustTextAreaHeight(e)}
                    onBlur={(e) => {
                        if (!isValidInput(prevTarget, e)) {
                            prevTarget.current = null
                            return
                        }
                        onUpdateFavourite(item)
                    }}
                />
            </td>
        )
    }

    const renderCheckBox = (item) => {
        return (
            <input
                type='checkbox'
                name='categories'
                checked={selectedIds.includes(item.id)}
                onChange={() => toggleSelection(item.id, item)}
                disabled={item?.isHidden || isSoldFolder()}
            />
        )
    }

    const renderProductImage = (item) => {
        const designDocuments = item?.isSet
            ? item?.setMemberDocUrls?.map((item) => ({
                  id: item,
                  docUrl: item
              }))
            : [{ id: item.docUrl, docUrl: item.docUrl }]
        return (
            <div className='flex items-center'>
                <div className='w-[72px] min-w-[72px]'>
                    <DesignImages
                        designInfo={{
                            designDocuments: designDocuments,
                            isSet: item?.isSet
                        }}
                    />
                </div>
                <div className='ml-2'>
                    {' '}
                    {item?.isPack && <PackSetOrTag tagName='Pack' />}
                    {item?.isSet && <PackSetOrTag tagName='Set' />}
                    <Tooltip
                        title={item?.referenceNumber}
                        placement={'top'}
                        arrow
                    >
                        <p
                            className='blue regular-14 cursor-pointer mt-1'
                            onClick={() => onClickDesign(item)}
                        >
                            {getShortName(item?.referenceNumber, 16)}
                        </p>
                    </Tooltip>
                </div>
            </div>
        )
    }

    const renderQuoteStatus = (item) => {
        return <>{item?.quoteStatus && getQuoteStatusColor(item.quoteStatus)}</>
    }

    const renderSampleStatus = (item) => {
        const sampleStatus =
            item?.sampleStatus === 'CANCELLED'
                ? item?.sampleStatus
                : item?.currentActivity
        return (
            <>
                {sampleStatus && (
                    <>
                        {getColorWiseActivityStatusOfSample(
                            sampleStatus === 'REQUESTED'
                                ? 'SAMPLE_REQUESTED'
                                : sampleStatus,
                            ''
                        )}
                    </>
                )}

                <p className='text-sm blue mb-1 mt-1'>
                    <Link to={`/samples/detail/${item?.sampleId}`}>
                        {item?.sampleRefNo}{' '}
                    </Link>
                </p>
            </>
        )
    }

    const renderOrderStatus = (item) => {
        return (
            <>
                {item?.orderStatus && (
                    <span
                        className={`status ${
                            getOrderStatus[item?.orderStatus]
                        }`}
                    >
                        {capitalizeFirstLetter(item?.orderStatus)}
                    </span>
                )}
                <p className='text-sm blue my-1'>
                    <Link to={`/orders/view/${item?.orderId}`}>
                        {item?.orderRefNos && item?.orderRefNos[0]}
                    </Link>
                </p>
                {item?.deliveryDate && (
                    <p className='text-sm gray_300'>
                        ETD:{' '}
                        {changeDateFormat(
                            item?.deliveryDate,
                            '',
                            'Do MMM, YYYY'
                        )}
                    </p>
                )}
            </>
        )
    }

    const isShowValidity = (item) => {
        if (item?.sampleStatus || item?.quoteStatus || item?.orderStatus) {
            return false
        }
        return true
    }

    const renderOfferPrice = (item) => {
        return (
            <>
                <div className='flex items-center'>
                    <p className='regular-16 gray_400 flex items-center mb-1'>
                        <span className='mr-1'>
                            ${item?.offerPrice.toFixed(2)}
                        </span>
                        {isShowValidity(item) && isPickAndChooseBuyer() && (
                            <Tooltip
                                title={
                                    <>
                                        <p className='regular-14 text-white'>
                                            *Price may vary.
                                        </p>
                                        <p className='regular-14 text-white'>
                                            *Applicable for MOQ 1000.
                                        </p>
                                    </>
                                }
                            >
                                <InfoIcon />
                            </Tooltip>
                        )}
                    </p>
                    {item?.priceUpdateDTOArray && (
                        <s className='text-[#C4C4C4] whitespace-nowrap ml-1'>
                            $
                            {item?.priceUpdateDTOArray[0].previousValue?.toFixed(
                                2
                            )}
                        </s>
                    )}
                </div>
            </>
        )
    }

    const renderTabView = () => {
        return (
            <div className='tab-view-table favourite-tab-view-table py-4 px-3'>
                <div className='select-all mb-2'>
                    <div className='flex items-center'>
                        <input
                            checked={isAllChecked()}
                            type='checkbox'
                            name='all'
                            onChange={toggleAllSelection}
                        />
                        <label className='text-base blue ml-2'>
                            Select all
                        </label>
                    </div>
                </div>
                <div className='all-rows'>
                    {favouriteData?.map((item) => {
                        return (
                            <div className='single-row mb-4' key={item.id}>
                                <table className='table'>
                                    <tr>
                                        <td rowSpan={9}>
                                            <div className='mt-2'>
                                                {renderCheckBox(item)}
                                            </div>
                                        </td>
                                        <td
                                            rowSpan={9}
                                            className={`eye-column cursor-pointer ${
                                                isSoldFolder()
                                                    ? 'cursor-none'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                handleProductShowHide(
                                                    item?.id,
                                                    !item.isHidden,
                                                    item?.addedToFavouriteById
                                                )
                                            }
                                        >
                                            <EyeIcon isHidden={item.isHidden} />
                                        </td>
                                        <td className='heading'>Style</td>
                                        <td>{renderProductImage(item)}</td>
                                    </tr>
                                    <tr>
                                        <td className='heading'>Description</td>
                                        {!item?.isSet && (
                                            <td>{item?.description}</td>
                                        )}
                                        {item?.isSet && (
                                            <td>
                                                <ul>
                                                    {item?.setMemberDescriptions?.map(
                                                        (description, i) => (
                                                            <li
                                                                key={`set_description_${i}`}
                                                            >
                                                                {description}
                                                            </li>
                                                        )
                                                    )}
                                                </ul>
                                            </td>
                                        )}
                                    </tr>
                                    <tr>
                                        <td className='heading'>Quantity</td>
                                        {renDerQuantity(item)}
                                    </tr>
                                    <tr>
                                        <td className='heading'>Quote</td>
                                        <td>
                                            {renderQuoteStatus(item)}
                                            <p className='text-sm blue mb-1'>
                                                <Link
                                                    to={`/quotes/detail/${item?.quoteId}`}
                                                >
                                                    {item?.quoteRefNo}
                                                </Link>
                                            </p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className='heading'>
                                            Target Price
                                        </td>
                                        <td>
                                            {item?.targetPrice && (
                                                <span className='gray_400'>
                                                    $
                                                    {item?.targetPrice.toFixed(
                                                        2
                                                    )}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className='heading'>Offer price</td>{' '}
                                        <td>
                                            {item?.offerPrice &&
                                                renderOfferPrice(item)}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td className='heading'>Sample</td>
                                        <td>{renderSampleStatus(item)}</td>
                                    </tr>
                                    <tr>
                                        <td className='heading'>Order</td>
                                        <td>{renderOrderStatus(item)}</td>
                                    </tr>
                                    <tr>
                                        <td className='heading'>Remarks</td>
                                        {renderRemarks(item)}
                                    </tr>
                                </table>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    const renderWebTableView = () => {
        const renderTableBody = () => {
            return sortedData?.map((item, index) => {
                let rowSpan = item?.setMemberDescriptions?.length || null
                let tmpArray = Array.from(
                    {
                        length: item?.setMemberDescriptions?.length || 1
                    },
                    (_, i) => i + 1
                )
                return (
                    <React.Fragment key={`product_${index}`}>
                        {tmpArray.map((_, row) => {
                            return (
                                <tr
                                    key={`product_${item.id}_${row}`}
                                    className={`${
                                        item?.isHidden ? 'row-hidden' : ''
                                    }`}
                                >
                                    {row === 0 && (
                                        <>
                                            <td
                                                rowSpan={rowSpan}
                                                className='checkbox-column'
                                            >
                                                {renderCheckBox(item)}
                                            </td>
                                            {isPickAndChooseBuyer() && (
                                                <td
                                                    rowSpan={rowSpan}
                                                    className={`eye-column cursor-pointer ${
                                                        isSoldFolder()
                                                            ? 'cursor-none'
                                                            : ''
                                                    }`}
                                                    onClick={() =>
                                                        handleProductShowHide(
                                                            item?.id,
                                                            !item.isHidden,
                                                            item?.addedToFavouriteById
                                                        )
                                                    }
                                                >
                                                    <EyeIcon
                                                        isHidden={item.isHidden}
                                                    />
                                                </td>
                                            )}
                                            <td
                                                rowSpan={rowSpan}
                                                className='relative style-column'
                                            >
                                                {renderProductImage(item)}
                                            </td>
                                            {!item?.isSet && (
                                                <td className='description-column'>
                                                    {item?.description}
                                                </td>
                                            )}
                                        </>
                                    )}

                                    {item?.isSet && (
                                        <td className='description-column'>
                                            {item?.setMemberDescriptions?.[row]}
                                        </td>
                                    )}
                                    {row === 0 && (
                                        <>
                                            {renDerQuantity(item, rowSpan)}
                                            <td
                                                rowSpan={rowSpan}
                                                className='quotes-column'
                                            >
                                                {renderQuoteStatus(item)}
                                                <p className='text-sm blue mb-1 mt-1'>
                                                    <Link
                                                        to={`/quotes/detail/${item?.quoteId}`}
                                                    >
                                                        {item?.quoteRefNo}
                                                    </Link>
                                                </p>
                                            </td>
                                            <td
                                                className='target-column'
                                                rowSpan={rowSpan}
                                            >
                                                {item?.targetPrice && (
                                                    <span className='regular-16 gray_400'>
                                                        $
                                                        {item?.targetPrice.toFixed(
                                                            2
                                                        )}
                                                    </span>
                                                )}
                                            </td>
                                            <td
                                                className='offer-price-column'
                                                rowSpan={rowSpan}
                                            >
                                                {item?.offerPrice &&
                                                    renderOfferPrice(item)}
                                            </td>
                                            <td
                                                rowSpan={rowSpan}
                                                className='sample-column'
                                            >
                                                {renderSampleStatus(item)}
                                            </td>
                                            <td
                                                rowSpan={rowSpan}
                                                className='order-column'
                                            >
                                                {renderOrderStatus(item)}
                                            </td>
                                            {renderRemarks(item, rowSpan)}
                                        </>
                                    )}
                                </tr>
                            )
                        })}
                    </React.Fragment>
                )
            })
        }

        return (
            <div className='px-4 mt-6'>
                <div className='group mb-3'>
                    {groupInfo && (
                        <div className='flex items-center mb-3'>
                            <h3 className='text-xl font-bold mr-4'>
                                {capitalizeFirstLetter(groupInfo?.name)}:
                                <span className='ml-1'>{groupInfo?.count}</span>
                            </h3>
                            <div className='flex items-center p-2 rounded bg-[#F8F8F8]'>
                                <p className='semibold-16'>
                                    {groupInfo?.totalQuantity} Units
                                </p>{' '}
                                <p className=' text-base weight-600 text-[#E9E9EA] mx-2'>
                                    |
                                </p>
                                <p className='semibold-16'>
                                    ${groupInfo?.totalPrice?.toFixed(2)}
                                </p>
                            </div>
                        </div>
                    )}
                    <table className='table favourite-list-table'>
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        checked={isAllChecked()}
                                        type='checkbox'
                                        name={categoryName}
                                        onChange={toggleAllSelection}
                                        disabled={isSoldFolder()}
                                    />
                                </th>
                                {isPickAndChooseBuyer() && <th></th>}
                                <th>Style</th>
                                <th>Description</th>
                                <th>
                                    <span className='mr-1'>Qty</span>{' '}
                                    <span
                                        className='cursor-pointer'
                                        onClick={handleSort}
                                    >
                                        <SortIcon sortOrder={sortOrder} />
                                    </span>
                                </th>
                                <th>Quote</th>
                                <th>Target</th>
                                <th className='whitespace-nowrap'>
                                    Offer Price
                                </th>
                                <th>Sample</th>
                                <th>Order</th>
                                <th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>{renderTableBody()}</tbody>
                    </table>
                </div>
            </div>
        )
    }

    return <>{screenWidth < 1200 ? renderTabView() : renderWebTableView()}</>
}

export default TableView
